// @leo/model — the canonical business model shared by every LEO CORE module.
// Principles: legal entity, channel and operating model are first-class on every location, PO and transaction;
// every synchronised record keeps its origin (system, key, as-of); money is decimal (string in transit), never float.
import { z } from 'zod';

export const SourceSystem = z.enum(['wizapp', 'tally', 'retailmaxx', 'tradeone', 'leo', 'excel', 'brand']);
export type SourceSystem = z.infer<typeof SourceSystem>;

export const Channel = z.enum(['retail', 'trade', 'key_account', 'sis', 'ecom', 'other', 'unallocated']);
export type Channel = z.infer<typeof Channel>;

/** Who owns and operates a selling location. FOFO tills are not the group's revenue; the supply invoice is. */
export const OperatingModel = z.enum(['COCO', 'COFO', 'FOFO', 'SIS', 'WAREHOUSE', 'OFFICE']);
export type OperatingModel = z.infer<typeof OperatingModel>;

export const Division = z.enum(['Footwear', 'Apparel', 'Accessories', 'Equipment', 'Other']);
export type Division = z.infer<typeof Division>;

export const Gender = z.enum(['Men', 'Women', 'Unisex', 'Boys', 'Girls', 'Kids']);
export type Gender = z.infer<typeof Gender>;

export const LegalEntity = z.object({
  leoId: z.string(),
  name: z.string(),
  shortCode: z.string(),
  gstins: z.array(z.string()).default([]),
  pan: z.string().optional(),
  tallyCompany: z.string().optional(),
  role: z.enum(['retail', 'distribution', 'buying', 'holding', 'other']),
  active: z.boolean().default(true),
});
export type LegalEntity = z.infer<typeof LegalEntity>;

export const Location = z.object({
  leoId: z.string(),
  code: z.string(),                 // e.g. MJPTR
  name: z.string(),
  kind: z.enum(['store', 'warehouse', 'counter', 'office']),
  operatingModel: OperatingModel,
  ownerEntityLeoId: z.string(),     // legal entity that owns stock on the floor
  billingEntityName: z.string().optional(), // the till's legal name in WizApp (e.g. "STEPS BHILWARA")
  business: z.string().optional(),  // Marathon / Kick Sports / Leeon
  city: z.string().optional(),
  state: z.string().optional(),
  region: z.string().optional(),
  sqft: z.number().int().optional(),
  openedOn: z.string().optional(),  // ISO date
  closedOn: z.string().optional(),
  active: z.boolean().default(true),
});
export type Location = z.infer<typeof Location>;

export const Brand = z.object({ leoId: z.string(), name: z.string(), aliases: z.array(z.string()).default([]) });
export type Brand = z.infer<typeof Brand>;

/** Governed product hierarchy node: Brand › Line › Series (family) — gender is an attribute, never a node. */
export const ProductFamily = z.object({
  leoId: z.string(),
  brandLeoId: z.string(),
  line: z.string().optional(),      // e.g. BOBS
  series: z.string(),               // e.g. BOBS Sport
  division: Division,
  status: z.enum(['proposed', 'approved', 'retired']).default('proposed'),
});
export type ProductFamily = z.infer<typeof ProductFamily>;

export const Article = z.object({
  leoId: z.string(),
  brandLeoId: z.string(),
  articleNo: z.string(),            // WizApp "Item Name" = style-colour number
  name: z.string(),
  colour: z.string().optional(),
  division: Division,
  category: z.string().optional(),
  subcategory: z.string().optional(),
  familyLeoId: z.string().optional(),
  gender: Gender.optional(),
  season: z.string().optional(),
  hsn: z.string().optional(),
  mrp: z.string().optional(),       // decimal as string
});
export type Article = z.infer<typeof Article>;

export const Sku = z.object({
  leoId: z.string(),
  articleLeoId: z.string(),
  upc: z.string(),
  size: z.string(),
  mrp: z.string().optional(),
  unit: z.enum(['pair', 'piece']).optional(),
});
export type Sku = z.infer<typeof Sku>;

/** Provenance carried by every synchronised record. */
export const Provenance = z.object({
  system: SourceSystem,
  sourceKey: z.string(),
  asOf: z.string(),                 // ISO date or timestamp the source states
  batchId: z.string().optional(),
  version: z.number().int().optional(),
});
export type Provenance = z.infer<typeof Provenance>;

/** A figure that knows whether it can be trusted. Every KPI the platform shows carries this. */
export const Figure = z.object({
  value: z.string().nullable(),     // null = unknown (never zero when data is missing)
  unit: z.enum(['INR', 'units', 'days', 'pct', 'ratio', 'count']),
  asOf: z.string().nullable(),
  provisional: z.boolean(),
  estimate: z.boolean(),
  coverage: z.number().min(0).max(1).nullable(), // share of expected inputs present
  sources: z.array(SourceSystem),
  note: z.string().optional(),
});
export type Figure = z.infer<typeof Figure>;

export const unknownFigure = (unit: Figure['unit'], note: string): Figure => ({ value: null, unit, asOf: null, provisional: true, estimate: false, coverage: 0, sources: [], note });
export * from './roles.js';
export * from './glossary.js';
export * from './ask.js';
export * from './scope.js';
export * from './explain.js';
export * from './festivals.js';
export * from './basis.js';
